import type { PresignResponse } from '@platewell/schema';

export type UploadState = 'pending' | 'uploading' | 'uploaded' | 'failed';

export interface PendingUpload {
  /** SHA-256 of the bytes, in lowercase hex. Also the R2 object key and the photo id. */
  blobId: string;
  householdId: string;
  /** Platform-specific pointer to the bytes: an object-store key on web, a file URI on native. */
  localRef: string;
  contentType: string;
  byteSize: number;
  width: number;
  height: number;
  blurhash: string;
  state: UploadState;
  attempts: number;
  nextAttemptAt: number;
  lastError?: string;
}

/** Durable queue of uploads that have to survive the app being killed mid-flight. */
export interface UploadQueueStore {
  put(entry: PendingUpload): Promise<void>;
  remove(blobId: string): Promise<void>;
  listReady(now: number): Promise<PendingUpload[]>;
  get(blobId: string): Promise<PendingUpload | null>;
}

/** Reads the local bytes for an entry. Web uses a Blob store, native reads the filesystem. */
export interface LocalBlobSource {
  read(localRef: string): Promise<Uint8Array>;
  /** Called once the bytes are safely in R2, so caches can be trimmed. */
  release?(localRef: string): Promise<void>;
}

export interface UploadQueueOptions {
  store: UploadQueueStore;
  blobs: LocalBlobSource;
  apiBaseUrl: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
  onChange?: (pending: number) => void;
  /**
   * React Native has no WebCrypto, so the native app supplies its own SHA-256. The hash has
   * to agree byte for byte across platforms — it is the object key both of them write to.
   */
  digest?: (bytes: Uint8Array) => Promise<string>;
}

const MAX_ATTEMPTS = 12;
const BASE_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 10 * 60 * 1_000;

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Offline-first photo uploads.
 *
 * A photo is referenced in the CRDT by the hash of its bytes the instant it is captured, so
 * the recipe is complete and shareable before any network exists. This queue then moves the
 * bytes to R2 whenever it can. The consequence worth stating plainly: a peer may briefly see
 * a photo id whose blob has not landed yet, so every photo view must tolerate a 404 and show
 * the local copy or a placeholder rather than an error.
 *
 * Content addressing also makes the whole pipeline idempotent. Retrying an upload, or two
 * devices uploading the same photo, converges on one object instead of duplicating it.
 */
export class PhotoUploadQueue {
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private readonly digest: (bytes: Uint8Array) => Promise<string>;
  private running = false;
  private stopped = false;
  private wakeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: UploadQueueOptions) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.now = options.now ?? (() => Date.now());
    this.digest = options.digest ?? sha256Hex;
  }

  /**
   * Registers bytes for upload and returns their content hash.
   *
   * Callers should write the returned id into the recipe immediately rather than waiting for
   * the upload; that is the whole point of hashing before sending.
   */
  async enqueue(input: {
    householdId: string;
    bytes: Uint8Array;
    localRef: string;
    contentType: string;
    width?: number;
    height?: number;
    blurhash?: string;
  }): Promise<string> {
    const blobId = await this.digest(input.bytes);
    const existing = await this.options.store.get(blobId);
    if (existing?.state === 'uploaded') return blobId;

    await this.options.store.put({
      blobId,
      householdId: input.householdId,
      localRef: input.localRef,
      contentType: input.contentType,
      byteSize: input.bytes.byteLength,
      width: input.width ?? 0,
      height: input.height ?? 0,
      blurhash: input.blurhash ?? '',
      state: 'pending',
      attempts: 0,
      nextAttemptAt: 0,
    });

    void this.drain();
    return blobId;
  }

  /** Processes every entry whose backoff has elapsed. Safe to call at any time. */
  async drain(): Promise<void> {
    if (this.running || this.stopped) return;
    this.running = true;
    try {
      let ready = await this.options.store.listReady(this.now());
      while (ready.length > 0 && !this.stopped) {
        for (const entry of ready) {
          await this.processOne(entry);
        }
        ready = await this.options.store.listReady(this.now());
      }
    } finally {
      this.running = false;
      this.options.onChange?.((await this.options.store.listReady(Number.MAX_SAFE_INTEGER)).length);
    }
  }

  private async processOne(entry: PendingUpload): Promise<void> {
    try {
      const presigned = await this.presign(entry);

      if (!presigned.alreadyUploaded && presigned.uploadUrl != null) {
        const bytes = await this.options.blobs.read(entry.localRef);
        // Local development answers with a path on the API rather than an R2 URL.
        const uploadUrl = presigned.uploadUrl.startsWith('/')
          ? `${this.options.apiBaseUrl}${presigned.uploadUrl}`
          : presigned.uploadUrl;
        const response = await this.fetchImpl(uploadUrl, {
          method: 'PUT',
          headers: { 'content-type': entry.contentType },
          body: bytes as unknown as BodyInit,
        });
        if (!response.ok) {
          throw new Error(`R2 upload failed with ${response.status}`);
        }
      }

      // R2 writes do not notify the Worker on their own, so the client closes the loop.
      // Until this lands the blob row has no uploaded_at and thumbnailing never starts.
      await this.fetchImpl(`${this.options.apiBaseUrl}/api/blobs/${entry.blobId}/uploaded`, {
        method: 'POST',
        credentials: 'include',
      });

      await this.options.store.remove(entry.blobId);
      await this.options.blobs.release?.(entry.localRef);
    } catch (error) {
      await this.recordFailure(entry, error);
    }
  }

  private async presign(entry: PendingUpload): Promise<PresignResponse> {
    const response = await this.fetchImpl(`${this.options.apiBaseUrl}/api/blobs/presign`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        blobId: entry.blobId,
        contentType: entry.contentType,
        byteSize: entry.byteSize,
        width: entry.width,
        height: entry.height,
        blurhash: entry.blurhash,
        householdId: entry.householdId,
      }),
    });
    if (!response.ok) throw new Error(`presign failed with ${response.status}`);
    return (await response.json()) as PresignResponse;
  }

  private async recordFailure(entry: PendingUpload, error: unknown): Promise<void> {
    const attempts = entry.attempts + 1;
    const message = error instanceof Error ? error.message : String(error);

    if (attempts >= MAX_ATTEMPTS) {
      // Give up on the transfer but keep the entry visible so the UI can offer a retry.
      // Silently dropping it would leave a recipe pointing at a blob that never arrives.
      await this.options.store.put({
        ...entry,
        attempts,
        state: 'failed',
        lastError: message,
        nextAttemptAt: Number.MAX_SAFE_INTEGER,
      });
      return;
    }

    const backoff = Math.min(BASE_BACKOFF_MS * 2 ** entry.attempts, MAX_BACKOFF_MS);
    const jitter = Math.random() * backoff * 0.25;
    await this.options.store.put({
      ...entry,
      attempts,
      state: 'pending',
      lastError: message,
      nextAttemptAt: this.now() + backoff + jitter,
    });

    this.scheduleWake(backoff + jitter);
  }

  private scheduleWake(delayMs: number): void {
    if (this.wakeTimer != null || this.stopped) return;
    this.wakeTimer = setTimeout(() => {
      this.wakeTimer = null;
      void this.drain();
    }, delayMs);
  }

  /** Clears the failure state so a user-initiated retry starts immediately. */
  async retry(blobId: string): Promise<void> {
    const entry = await this.options.store.get(blobId);
    if (entry == null) return;
    await this.options.store.put({ ...entry, state: 'pending', attempts: 0, nextAttemptAt: 0 });
    void this.drain();
  }

  stop(): void {
    this.stopped = true;
    if (this.wakeTimer != null) clearTimeout(this.wakeTimer);
    this.wakeTimer = null;
  }
}
